import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import * as persisted from '#/state/persisted'
import {flagWithDefault} from '#/state/persisted/schema'

type StateContext = boolean
type SetContext = (v: boolean) => void

/**
 * The stored choice, or the default when the reader has never made one.
 *
 * Absent must not read as "off": the state already written for everyone who
 * used the app before this preference existed has no key for it. See
 * `flagWithDefault`.
 */
function read(): boolean {
  return flagWithDefault(
    persisted.get('flashbookPageAnimation'),
    persisted.defaults.flashbookPageAnimation,
  )
}

const stateContext = createContext<StateContext>(read())
stateContext.displayName = 'FlashBookPageAnimationStateContext'
const setContext = createContext<SetContext>((_: boolean) => {})
setContext.displayName = 'FlashBookPageAnimationSetContext'

export function Provider({children}: {children: React.ReactNode}) {
  const [state, setState] = useState(read)

  const setStateWrapped = useCallback(
    (enabled: persisted.Schema['flashbookPageAnimation']) => {
      setState(Boolean(enabled))
      persisted.write('flashbookPageAnimation', enabled)
    },
    [setState],
  )

  useEffect(() => {
    return persisted.onUpdate(
      'flashbookPageAnimation',
      nextFlashbookPageAnimation => {
        setState(nextFlashbookPageAnimation !== false)
      },
    )
  }, [setStateWrapped])

  return (
    <stateContext.Provider value={state}>
      <setContext.Provider value={setStateWrapped}>
        {children}
      </setContext.Provider>
    </stateContext.Provider>
  )
}

/** Whether FlashBook readers may animate page turns. Device-wide. */
export const useFlashBookPageAnimation = () => useContext(stateContext)
export const useSetFlashBookPageAnimation = () => useContext(setContext)
